import { useAuthStore } from '@/store/auth'
import { useTeamStore } from '@/store/team'

// API base URL - empty string uses relative paths through Vite proxy
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface ApiErrorResponse {
  code: string
  message: string
  details?: unknown
  fields?: Record<string, string>
  request_id?: string
}

export class ApiError extends Error {
  status: number
  statusText: string
  data?: ApiErrorResponse
  code?: string
  fields?: Record<string, string>
  requestId?: string

  constructor(status: number, statusText: string, data?: unknown) {
    super(`API Error: ${status} ${statusText}`)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText

    // Parse error response if available
    if (data && typeof data === 'object') {
      this.data = data as ApiErrorResponse
      this.code = this.data.code
      this.fields = this.data.fields
      this.requestId = this.data.request_id
    }
  }

  /**
   * Get user-friendly error message from API response.
   *
   * Priority:
   * 1. Use backend message (primary source of truth)
   * 2. Fallback to code-based message (for client-side errors)
   * 3. Generic message based on HTTP status
   */
  getUserMessage(): string {
    // Primary: Use backend's user-friendly message
    if (this.data?.message) {
      return this.data.message
    }

    // Fallback: For client-side errors or missing backend messages
    if (this.code) {
      return this.getMessageFromCode(this.code)
    }

    // Last resort: Generic HTTP status message
    return this.getGenericMessage()
  }

  /**
   * Check if this error has field-level validation errors
   */
  hasFieldErrors(): boolean {
    return !!this.fields && Object.keys(this.fields).length > 0
  }

  /**
   * Get field-level error for a specific field
   */
  getFieldError(fieldName: string): string | undefined {
    return this.fields?.[fieldName]
  }

  /**
   * Fallback messages for error codes.
   *
   * NOTE: Backend should return user-friendly messages in ErrorResponse.message.
   * These are only used when:
   * 1. Client-side errors (NETWORK_ERROR)
   * 2. Backend message is missing (shouldn't happen)
   * 3. Future localization/i18n centralization
   */
  private getMessageFromCode(code: string): string {
    const codeMessages: Record<string, string> = {
      // Client-side errors (no backend message)
      'NETWORK_ERROR': 'Unable to connect. Please check your internet connection and try again.',
      'UNKNOWN_ERROR': 'An unexpected error occurred. Please try again.',

      // Backend error codes (should have message from backend, these are fallbacks)
      // Auth errors
      'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
      'INVALID_TOKEN': 'Your session has expired. Please sign in again.',
      'TOKEN_EXPIRED': 'Your session has expired. Please sign in again.',
      'REFRESH_TOKEN_EXPIRED': 'Your session has expired. Please sign in again.',
      'INVALID_TWO_FACTOR_CODE': 'Invalid two-factor authentication code. Please try again.',

      // User errors
      'USER_NOT_FOUND': 'User not found. Please check your credentials.',
      'USER_ALREADY_EXISTS': 'This email is already registered. Try signing in instead.',
      'EMAIL_NOT_VERIFIED': 'Please verify your email before signing in. Check your inbox for the verification link.',

      // Account status
      'ACCOUNT_LOCKED': 'Your account is temporarily locked. Please try again later.',
      'ACCOUNT_SUSPENDED': 'Your account has been suspended. Please contact support.',

      // Validation
      'INVALID_EMAIL': 'Please enter a valid email address.',
      'INVALID_PASSWORD': 'Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, and a number.',
      'VALIDATION_ERROR': 'Please fix the errors below and try again.',

      // System
      'RATE_LIMITED': 'Too many requests. Please wait a moment and try again.',
      'INTERNAL_ERROR': 'Something went wrong on our end. Please try again later.',
      'SYSTEM_UNAVAILABLE': 'Service temporarily unavailable. Please try again in a moment.',
      'PROVIDER_ERROR': 'Payment provider returned an error. Please try again in a few minutes or choose a different cryptocurrency.',
      'PROVIDER_UNAVAILABLE': 'Payment provider is temporarily unavailable. Please try again later.',
      'SESSION_NOT_FOUND': 'Session not found. Please sign in again.',
      'TWO_FACTOR_REQUIRED': 'Two-factor authentication is required for your account.',
      'PUBLISH_FAILED': 'Failed to publish the site. Please check your site settings and try again.',
    }

    return codeMessages[code] || `Error: ${code.replace(/_/g, ' ').toLowerCase()}`
  }

  private getGenericMessage(): string {
    switch (this.status) {
      case 400:
        return 'Invalid request. Please check your input.'
      case 401:
        return 'Your session has expired. Please sign in again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'Resource not found. It may have been deleted.'
      case 409:
        return 'This action conflicts with existing data.'
      case 422:
        return 'Please fix the errors in the form and try again.'
      case 429:
        return 'Too many requests. Please wait a moment and try again.'
      case 500:
        return 'Something went wrong. Please try again later.'
      case 503:
        return 'Service temporarily unavailable. Please try again in a moment.'
      default:
        return 'An unexpected error occurred. Please try again.'
    }
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

// Refresh lock to prevent concurrent refresh calls (which trigger
// refresh-token-reuse detection and revoke all sessions).
let refreshPromise: Promise<string | null> | null = null

async function refreshTokenIfNeeded(): Promise<string | null> {
  // Before starting a new refresh, check if another tab already refreshed
  // by re-reading the persisted state from localStorage.
  const persisted = readPersistedAuth()
  if (persisted?.token) {
    const current = useAuthStore.getState()
    if (persisted.token !== current.token) {
      // Another tab refreshed — adopt the new tokens and skip refresh
      useAuthStore.getState().setTokens(
        persisted.token,
        persisted.refreshToken ?? '',
        persisted.tokenExpiresAt ?? undefined,
      )
      return persisted.token
    }
  }

  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = doRefreshToken()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

/** Read auth state directly from localStorage (written by other tabs). */
function readPersistedAuth(): {
  token: string | null
  refreshToken: string | null
  tokenExpiresAt: number | null
} | null {
  try {
    const raw = localStorage.getItem('ghostflow-auth')
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      state?: {
        token?: string | null
        refreshToken?: string | null
        tokenExpiresAt?: number | null
      }
    }
    return {
      token: parsed.state?.token ?? null,
      refreshToken: parsed.state?.refreshToken ?? null,
      tokenExpiresAt: parsed.state?.tokenExpiresAt ?? null,
    }
  } catch {
    return null
  }
}

async function doRefreshToken(): Promise<string | null> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState()

  if (!refreshToken) {
    logout()
    return null
  }

  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: refreshToken }),
    })

    if (!response.ok) {
      logout()
      return null
    }

    const data = await response.json() as {
      accessToken: string
      refreshToken: string
      accessTokenExpiresAt?: number
    }
    setTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresAt)
    return data.accessToken
  } catch {
    logout()
    return null
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options
  const { token } = useAuthStore.getState()
  const { currentTeamId } = useTeamStore.getState()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  }

  if (!skipAuth && token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  // Add team context header for team-scoped requests
  // Fall back to user ID for admin users without a team
  const teamId = currentTeamId || useAuthStore.getState().user?.id
  if (teamId && !endpoint.includes('/auth/')) {
    (headers as Record<string, string>)['X-Team-ID'] = teamId
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`

  let response: Response

  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      // Prevent browser from caching API responses (stale 301/401 etc.)
      cache: 'no-store',
    })
  } catch (error) {
    // Network errors (no internet, CORS, etc.)
    console.error('Network error:', error)
    throw new ApiError(0, 'Network Error', {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect. Please check your internet connection and try again.',
    })
  }

  // If unauthorized, check if it's a token error before refreshing.
  // Only refresh for token-related 401s (INVALID_TOKEN, TOKEN_EXPIRED).
  // Other 401s (e.g., INVALID_CREDENTIALS for wrong password) should NOT
  // trigger a refresh — they should be thrown immediately.
  if (response.status === 401 && !skipAuth) {
    const cloned = response.clone()
    const errorData = await cloned.json().catch(() => null) as { code?: string } | null
    const errorCode = errorData?.code
    // Only refresh for token-related 401s. If the response isn't JSON (errorData is null),
    // it's not a token error — it's a service-specific error (e.g., missing team context).
    const isTokenError = errorData !== null && (!errorCode || errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_EXPIRED')

    if (isTokenError) {
      const newToken = await refreshTokenIfNeeded()
      if (newToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
        try {
          response = await fetch(url, {
            ...fetchOptions,
            headers,
            cache: 'no-store',
          })
        } catch (error) {
          console.error('Network error on retry:', error)
          throw new ApiError(0, 'Network Error', {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect. Please check your internet connection and try again.',
          })
        }
      }
    }
  }

  if (!response.ok) {
    let data = await response.json().catch(() => ({
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred. Please try again.',
    }))

    // Handle array of validation errors from Zod/validator
    if (Array.isArray(data)) {
      const fields: Record<string, string> = {}
      data.forEach((error: { path?: (string | number)[]; message?: string }) => {
        if (error.path && error.path.length > 0 && error.message) {
          const fieldName = error.path[error.path.length - 1]
          if (typeof fieldName === 'string') {
            fields[fieldName] = error.message
          }
        }
      })

      data = {
        code: 'VALIDATION_ERROR',
        message: 'Please fix the errors below and try again.',
        fields,
      }
    }

    throw new ApiError(response.status, response.statusText, data)
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return undefined as T
  }

  return JSON.parse(text) as T
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),

  /**
   * Upload a file as base64-encoded JSON payload.
   * Used for avatar uploads and similar small file uploads.
   * Sends: { data: "data:image/jpeg;base64,..." }
   */
  upload: <T>(endpoint: string, data: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify({ data }),
    }),

  /**
   * Make a request that returns a Blob (for file downloads).
   * Uses the same auth/team-context/retry logic as other api methods.
   */
  blob: async (endpoint: string, data?: unknown, options?: RequestOptions): Promise<Blob> => {
    const { skipAuth = false, ...fetchOptions } = options || {}
    const { token } = useAuthStore.getState()
    const { currentTeamId } = useTeamStore.getState()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string> || {}),
    }

    if (!skipAuth && token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const blobTeamId = currentTeamId || useAuthStore.getState().user?.id
    if (blobTeamId && !endpoint.includes('/auth/')) {
      headers['X-Team-ID'] = blobTeamId
    }

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`

    let response = await fetch(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      cache: 'no-store',
    })

    // Retry with refreshed token on 401 (only for token errors)
    if (response.status === 401 && !skipAuth) {
      const cloned = response.clone()
      const errorData = await cloned.json().catch(() => null) as { code?: string } | null
      const errorCode = errorData?.code
      const isTokenError = errorData !== null && (!errorCode || errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_EXPIRED')
      if (isTokenError) {
        const newToken = await refreshTokenIfNeeded()
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`
          response = await fetch(url, {
            method: 'POST',
            headers,
            body: data ? JSON.stringify(data) : undefined,
            cache: 'no-store',
          })
        }
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        code: 'EXPORT_FAILED',
        message: 'Failed to export report. Please try again.',
      }))
      throw new ApiError(response.status, response.statusText, errorData)
    }

    return response.blob()
  },

  /**
   * Make a GET request that returns a Blob (for file downloads like invoice PDFs).
   * Uses the same auth/team-context/retry logic as other api methods.
   */
  getBlob: async (endpoint: string, options?: RequestOptions): Promise<Blob> => {
    const { skipAuth = false, ...fetchOptions } = options || {}
    const { token } = useAuthStore.getState()
    const { currentTeamId } = useTeamStore.getState()

    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string> || {}),
    }

    if (!skipAuth && token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const getBlobTeamId = currentTeamId || useAuthStore.getState().user?.id
    if (getBlobTeamId && !endpoint.includes('/auth/')) {
      headers['X-Team-ID'] = getBlobTeamId
    }

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`

    let response = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-store',
    })

    // Retry with refreshed token on 401 (only for token errors)
    if (response.status === 401 && !skipAuth) {
      const cloned = response.clone()
      const errorData = await cloned.json().catch(() => null) as { code?: string } | null
      const errorCode = errorData?.code
      const isTokenError = errorData !== null && (!errorCode || errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_EXPIRED')
      if (isTokenError) {
        const newToken = await refreshTokenIfNeeded()
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`
          response = await fetch(url, {
            method: 'GET',
            headers,
            cache: 'no-store',
          })
        }
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        code: 'DOWNLOAD_FAILED',
        message: 'Failed to download file. Please try again.',
      }))
      throw new ApiError(response.status, response.statusText, errorData)
    }

    return response.blob()
  },
}
